import { CARD_BY_ID, CARD_MAGNITUDE, escalation, rulesFor } from '@horde/engine';
import type { CardId, GameEvent, GameState, Owner, PlayerId, TerritoryId } from '@horde/engine';
import { worldMap } from '@horde/map-world';

const names = new Map(worldMap.territories.map((t) => [t.id, t.name]));

export function territoryName(id: TerritoryId): string {
  return names.get(id) ?? id;
}

export function ownerName(state: GameState, owner: Owner): string {
  if (owner === 'horde') return 'the Horde';
  if (owner === null) return 'nobody';
  return state.players.find((p) => p.id === owner)?.name ?? owner;
}

export function playerName(state: GameState, id: PlayerId | null): string {
  if (!id) return '';
  return state.players.find((p) => p.id === id)?.name ?? id;
}

/** "You win" but "Ripley wins". */
export function winsVerb(name: string): string {
  return name.trim().toLowerCase() === 'you' ? 'win' : 'wins';
}

export function multiplier(state: GameState): number {
  return escalation(rulesFor(state.config.difficulty), state.round);
}

/** Card text with `{n}` filled in for the current round. */
export function cardText(cardId: CardId, state: GameState): string {
  const def = CARD_BY_ID[cardId];
  if (!def) return cardId;
  const base = CARD_MAGNITUDE[cardId];
  const n = base ? base * multiplier(state) : 0;
  return def.description.replace('{n}', String(n));
}

export type Tone = 'neutral' | 'good' | 'bad' | 'horde' | 'heading';

export interface Described {
  text: string;
  tone: Tone;
}

/** Human-readable log line for an event, or null for events that are noise. */
export function describeEvent(e: GameEvent, state: GameState): Described | null {
  const t = territoryName;
  switch (e.type) {
    case 'phaseChanged':
      if (e.phase === 'reinforce') {
        return {
          text: `Round ${e.round}: ${playerName(state, e.playerId)}'s turn.`,
          tone: 'neutral',
        };
      }
      return null;
    case 'risingStarted': {
      // Headings that group the log under each rising.
      if (e.playerId === null)
        return { text: `The horde rises · end of round ${e.round}`, tone: 'heading' };
      const name = playerName(state, e.playerId);
      return {
        text: `Zombie Rising · ${name === 'You' ? 'Your' : `${name}'s`} territories`,
        tone: 'heading',
      };
    }
    case 'reinforced':
      return {
        text: `${playerName(state, e.playerId)} reinforces ${t(e.territoryId)} with ${e.count}.`,
        tone: 'neutral',
      };
    case 'cardDrawn': {
      const def = CARD_BY_ID[e.cardId];
      // Online, another player's kept card arrives as a placeholder.
      if (!def)
        return {
          text: `${playerName(state, e.playerId)} draws a card and keeps it.`,
          tone: 'neutral',
        };
      return {
        text: `${playerName(state, e.playerId)} draws ${def?.name ?? e.cardId}.`,
        tone: def?.polarity === 'bad' ? 'bad' : 'good',
      };
    }
    case 'cardDiscarded':
      return {
        text: `${playerName(state, e.playerId)}'s hand is full; ${CARD_BY_ID[e.cardId]?.name} is discarded.`,
        tone: 'neutral',
      };
    case 'cardResolved':
      if (!e.target) return null;
      return {
        text: `${playerName(state, e.playerId)} plays ${CARD_BY_ID[e.cardId]?.name} on ${t(e.target)}.`,
        tone: 'good',
      };
    case 'diceRolled': {
      const where = e.from === e.to ? `purges ${t(e.from)}` : `${t(e.from)} → ${t(e.to)}`;
      return {
        text: `${where}: ${e.attacker.join(' ')} vs ${e.defender.join(' ')} · attacker −${e.attackerLosses}, defender −${e.defenderLosses}`,
        tone: 'neutral',
      };
    }
    case 'territoryCaptured':
      return {
        text: `${ownerName(state, e.to)} takes ${t(e.territoryId)} from ${ownerName(state, e.from)}.`,
        tone: 'good',
      };
    case 'zombiesRemoved':
      return { text: `${e.count} zombies destroyed in ${t(e.territoryId)}.`, tone: 'good' };
    case 'armiesLost':
      return { text: `${e.count} units lost in ${t(e.territoryId)}.`, tone: 'bad' };
    case 'zombiesMoved':
      return {
        text: `${e.count} zombies shamble from ${t(e.from)} into ${t(e.to)}.`,
        tone: 'horde',
      };
    case 'fortified':
      return { text: `${e.count} units move from ${t(e.from)} to ${t(e.to)}.`, tone: 'neutral' };
    case 'zombiesSpawned':
      return {
        text: `${e.count} zombie${e.count === 1 ? '' : 's'} rise in ${t(e.territoryId)}.`,
        tone: 'horde',
      };
    case 'bitten':
      return { text: `Zombies bite ${e.count} in ${t(e.territoryId)}.`, tone: 'bad' };
    case 'overrun':
      return { text: `${t(e.territoryId)} is overrun by the horde.`, tone: 'horde' };
    case 'hordeSpread':
      return null;
    case 'playerEliminated':
      return { text: `${playerName(state, e.playerId)} has fallen.`, tone: 'bad' };
    case 'gameEnded':
      if (e.outcome.kind === 'horde')
        return { text: `The world fell on round ${e.outcome.fellOnRound}.`, tone: 'horde' };
      {
        const name = playerName(state, e.outcome.winner);
        return { text: `${name} ${winsVerb(name)} by ${e.outcome.by}.`, tone: 'good' };
      }
  }
}

export const DIFFICULTY_BLURB: Record<string, string> = {
  calm: 'Zombies are a nuisance. Learn the game.',
  uneasy: 'The dead keep coming, but they can be managed.',
  outbreak: 'A real threat. Neglect a border and lose it.',
  apocalypse: 'The world is probably ending. Prove otherwise.',
};
